import {
  canonicalFieldForKey,
  canonicalValueForField,
  cylinderKeys,
} from "./schema.js";
import {
  averageValues,
} from "./math.js";

const ENGINE_SCALAR_METRICS = Object.freeze([
  "fuelFlow",
  "fuelUsed",
  "fuelPressure",
  "rpm",
  "map",
  "oilTemp",
  "oilPressure",
  "amps",
  "tit",
  "percentPower",
  "powerRopHp",
  "percentPowerRop",
  "powerLopHp",
  "percentPowerLop",
  "carbTemp",
  "inductionTemp",
  "compressorDischargeTemp",
]);

const twinEngineEvidenceChannelKeys = Object.freeze([
  "fuelFlow2",
  "fuelUsed2",
  "fuelPressure2",
  "oilTemp2",
  "oilPressure2",
  "rpm2",
  "map2",
  "tit2",
  "fuelFlowRight",
  "fuelUsedRight",
  "oilTempRight",
  "oilPressureRight",
  "rpmRight",
  "mapRight",
]);

const singleEngineChannels = {
  fuelFlow: "fuelFlow",
  fuelUsed: "fuelUsed",
  fuelPressure: "fuelPressure",
  rpm: "rpm",
  map: "map",
  oilTemp: "oilTemp",
  oilPressure: "oilPressure",
  amps: "amps",
  tit: "tit",
};

const engineOneChannels = {
  fuelFlow: "fuelFlow1",
  fuelUsed: "fuelUsed1",
  fuelPressure: "fuelPressure1",
  rpm: "rpm1",
  map: "map1",
  oilTemp: "oilTemp1",
  oilPressure: "oilPressure1",
  amps: "amps1",
  tit: "tit1",
};

const engineTwoChannels = {
  fuelFlow: "fuelFlow2",
  fuelUsed: "fuelUsed2",
  fuelPressure: "fuelPressure2",
  rpm: "rpm2",
  map: "map2",
  oilTemp: "oilTemp2",
  oilPressure: "oilPressure2",
  amps: "amps2",
  tit: "tit2",
};

const singleEngineIndexedAggregateChannels = Object.freeze({
  fuelFlow1: "fuelFlow",
  fuelFlowLeft: "fuelFlow",
  fuelUsed1: "fuelUsed",
  fuelUsedLeft: "fuelUsed",
  fuelPressure1: "fuelPressure",
  rpm1: "rpm",
  rpmLeft: "rpm",
  map1: "map",
  mapLeft: "map",
  oilTemp1: "oilTemp",
  oilTempLeft: "oilTemp",
  oilPressure1: "oilPressure",
  oilPressureLeft: "oilPressure",
  amps1: "amps",
  ampsLeft: "amps",
  tit1: "tit",
  titLeft: "tit",
});

const engineDescriptorCacheByPoints = new WeakMap();

export const ENGINE_LABEL_MODES = Object.freeze({
  LEFT_RIGHT: "left-right",
  FRONT_REAR: "front-rear",
});

export const ENGINE_LABEL_STYLES = Object.freeze({
  COMPACT: "compact",
  NARRATIVE: "narrative",
});

const twinEngineLabelSchemes = Object.freeze({
  [ENGINE_LABEL_MODES.LEFT_RIGHT]: [
    { label: "Left Engine", shortLabel: "L" },
    { label: "Right Engine", shortLabel: "R" },
  ],
  [ENGINE_LABEL_MODES.FRONT_REAR]: [
    { label: "Front Engine", shortLabel: "F" },
    { label: "Rear Engine", shortLabel: "R" },
  ],
});

// Un-modeled engine context inputs (parser metadata, profile-derived options).
// Typed loosely for now; the full aircraft-profile / parser-metadata shapes are
// a later migration step.
type EngineContextOptions = Record<string, any>;

const finite = (value: unknown): boolean => Number.isFinite(value);

function hasFiniteChannel(points, key) {
  return points.some((point) => finite(canonicalValueForField(point, key)));
}

function profileEngineEntries(profile) {
  const entries = profile?.engine?.engines;
  return Array.isArray(entries) ? entries.slice(0, 2) : [];
}

export function profileEngineCount(profile) {
  const count = Number(profile?.engine?.count);
  if (Number.isInteger(count) && count > 0) return Math.min(count, 2);
  const engines = profileEngineEntries(profile);
  if (engines.length) return Math.min(engines.length, 2);
  return null;
}

function normalizeEngineLayout(layout) {
  if (!layout || typeof layout !== "object") return null;
  const count = Number(layout.count ?? layout.engineCount);
  if (!Number.isInteger(count) || count <= 0) return null;
  return {
    ...layout,
    count: Math.min(count, 2),
  };
}

function profileEngineLayout(profile) {
  const explicit = normalizeEngineLayout(profile?.engineLayout ?? profile?.engine?.engineLayout ?? profile?.engine?.layout);
  if (explicit) return explicit;
  const engines = profileEngineEntries(profile);
  const count = profileEngineCount(profile);
  if (!count) return null;
  return {
    count,
    engineIds: count === 2 ? ["left", "right"] : ["engine1"],
    cylindersPerEngine: engines.length
      ? engines.slice(0, count).map((engine) => Number(engine?.cylinders) || Number(profile?.engine?.cylinders) || 0)
      : Array.from({ length: count }, () => Number(profile?.engine?.cylinders) || 0),
  };
}

function parserEngineLayout(options: EngineContextOptions = {}) {
  return normalizeEngineLayout(options.engineLayout ?? options.parserMetadata?.engineLayout);
}

export function normalizeEngineLabelMode(value) {
  const normalized = String(value ?? "").trim().toLowerCase().replace(/[_\s]+/g, "-");
  if (
    ["front-rear", "front/rear", "fore-aft", "fore/aft", "multi-centerline", "centerline", "centerline-thrust"].includes(normalized)
    || /(?:cessna-)?337|skymaster|push-?pull/.test(normalized)
  ) return ENGINE_LABEL_MODES.FRONT_REAR;
  return ENGINE_LABEL_MODES.LEFT_RIGHT;
}

function engineLabelModeFromContext(profile, options: EngineContextOptions = {}) {
  const aircraftText = `${profile?.aircraft?.type ?? ""} ${profile?.aircraft?.make ?? ""} ${profile?.aircraft?.model ?? ""}`;
  return normalizeEngineLabelMode(
    options.engineLabelMode
      ?? options.parserMetadata?.engineLabelMode
      ?? options.parserMetadata?.engineLayout?.labelMode
      ?? options.parserMetadata?.engineLayout?.engineLabelMode
      ?? profile?.engineLabelMode
      ?? profile?.engine?.labelMode
      ?? profile?.engineLayout?.labelMode
      ?? profile?.engineLayout?.engineLabelMode
      ?? profile?.engine?.engineLayout?.labelMode
      ?? profile?.engine?.layout?.labelMode
      ?? aircraftText,
  );
}

function twinEngineLabelScheme(profile, options: EngineContextOptions = {}) {
  return twinEngineLabelSchemes[engineLabelModeFromContext(profile, options)] ?? twinEngineLabelSchemes[ENGINE_LABEL_MODES.LEFT_RIGHT];
}

function inferredSingleCylinderCount(points, profile) {
  const profileCount = Number(profile?.engine?.cylinders);
  if (Number.isInteger(profileCount) && profileCount > 0) return Math.min(profileCount, 16);
  for (let number = 16; number >= 1; number -= 1) {
    if (hasFiniteChannel(points, `egt${number}`) || hasFiniteChannel(points, `cht${number}`)) return number;
  }
  return 0;
}

function engineParserId(options) {
  return String(options?.parserId ?? options?.parser ?? options?.parserMetadata?.parserId ?? "").trim();
}

function cylinderDescriptors(points, startNumber, maxCount, displayOffset = 0) {
  return cylinderDescriptorsForSources(
    points,
    Array.from({ length: maxCount }, (_, index) => startNumber + index),
    displayOffset,
  );
}

function cylinderDescriptorsForSources(points, sourceNumbers, displayOffset = 0) {
  return sourceNumbers.map((rawSourceNumber, index) => {
    const sourceNumber = Number(rawSourceNumber);
    if (!Number.isInteger(sourceNumber) || sourceNumber <= 0) return null;
    const egtKey = `egt${sourceNumber}`;
    const chtKey = `cht${sourceNumber}`;
    if (!hasFiniteChannel(points, egtKey) && !hasFiniteChannel(points, chtKey)) return null;
    return {
      number: index + 1 + displayOffset,
      sourceNumber,
      egtKey,
      chtKey,
    };
  }).filter(Boolean);
}

function layoutGroupNumbers(layout, family, index) {
  const groups = family === "cht" ? layout?.chtGroups : layout?.egtGroups;
  const group = Array.isArray(groups) ? groups[index] : null;
  return Array.isArray(group)
    ? group.map(Number).filter((number) => Number.isInteger(number) && number > 0)
    : [];
}

function layoutCylinderSourceNumbers(layout, index) {
  const explicit = [...new Set([
    ...layoutGroupNumbers(layout, "egt", index),
    ...layoutGroupNumbers(layout, "cht", index),
  ])].sort((a, b) => a - b);
  if (explicit.length) return explicit;

  const counts = Array.isArray(layout?.cylindersPerEngine) ? layout.cylindersPerEngine : [];
  const count = Number(counts[index]);
  if (!Number.isInteger(count) || count <= 0) return null;
  const start = counts.slice(0, index).reduce((total, item) => total + (Number.isInteger(Number(item)) ? Number(item) : 0), 1);
  return Array.from({ length: count }, (_, offset) => start + offset);
}

function explicitEngineLayout(profile, options) {
  return parserEngineLayout(options) ?? profileEngineLayout(profile);
}

function presentThermalSourceNumbers(points) {
  const numbers = new Set<number>();
  for (const point of points ?? []) {
    for (const key of Object.keys(point ?? {})) {
      const match = key.match(/^(?:egt|cht)(\d+)$/);
      const number = match ? Number(match[1]) : null;
      if (Number.isInteger(number) && number !== null && number > 0 && finite(point[key])) numbers.add(number);
    }
  }
  return [...numbers].sort((a, b) => a - b);
}

function layoutHasThermalMapping(layout) {
  if (!layout) return false;
  const egtGroups = Array.isArray(layout.egtGroups) ? layout.egtGroups : [];
  const chtGroups = Array.isArray(layout.chtGroups) ? layout.chtGroups : [];
  const grouped = [...egtGroups, ...chtGroups].some((group) => Array.isArray(group) && group.length > 0);
  const counts = Array.isArray(layout.cylindersPerEngine) && layout.cylindersPerEngine.some((count) => Number(count) > 0);
  return grouped || counts;
}

function engineLayoutCandidatesForProbeCount(probeCount) {
  if (probeCount === 8) return ["single-8", "twin-4"];
  if (probeCount === 12) return ["single-12", "twin-6"];
  if (probeCount === 16) return ["single-16", "twin-8"];
  return [];
}

export function engineLayoutAmbiguityForData(points = [], profile = null, options: EngineContextOptions = {}) {
  const safePoints = Array.isArray(points) ? points : [];
  const layout = explicitEngineLayout(profile, options);
  if (layoutHasThermalMapping(layout)) {
    return {
      ambiguous: false,
      status: "resolved",
      source: parserEngineLayout(options) ? "parser-metadata" : "profile",
      probeCount: null,
      candidates: [],
    };
  }

  const sourceNumbers = presentThermalSourceNumbers(safePoints);
  const probeCount = sourceNumbers.length ? Math.max(...sourceNumbers) : 0;
  const candidates = engineLayoutCandidatesForProbeCount(probeCount);
  if (!candidates.length) {
    return {
      ambiguous: false,
      status: probeCount ? "not-ambiguous" : "no-thermal-probes",
      source: "data",
      probeCount,
      sourceNumbers,
      candidates: [],
    };
  }

  return {
    ambiguous: true,
    status: "needs-engine-layout",
    source: "data",
    probeCount,
    sourceNumbers,
    candidates,
  };
}

function twinDetectionResult(points, profile, options: EngineContextOptions = {}) {
  const parserLayout = parserEngineLayout(options);
  if (parserLayout?.count === 2) return { detected: true, source: "parser-metadata" };
  if (parserLayout?.count === 1) return { detected: false, source: "parser-metadata" };
  const layout = profileEngineLayout(profile);
  if (layout?.count === 2) return { detected: true, source: "profile" };
  if (layout?.count === 1) return { detected: false, source: "profile" };
  if (engineParserId(options) === "insight-g4-twin") return { detected: true, source: "parser" };
  if (twinEngineEvidenceChannelKeys.some((key) => hasFiniteChannel(points, key))) return { detected: true, source: "side-channel" };
  if (options.allowThermalTwinInference !== true) return { detected: false, source: "single" };
  const hasLeftThermal = Array.from({ length: 6 }, (_, index) => index + 1)
    .some((number) => hasFiniteChannel(points, `egt${number}`) || hasFiniteChannel(points, `cht${number}`));
  const hasRightThermal = Array.from({ length: 6 }, (_, index) => index + 7)
    .some((number) => hasFiniteChannel(points, `egt${number}`) || hasFiniteChannel(points, `cht${number}`));
  return hasLeftThermal && hasRightThermal
    ? { detected: true, source: "thermal-fallback" }
    : { detected: false, source: "single" };
}

function descriptorCacheContextKey(options: EngineContextOptions = {}) {
  const layout = parserEngineLayout(options);
  return [
    engineParserId(options),
    layout ? JSON.stringify(layout) : "",
    normalizeEngineLabelMode(
      options.engineLabelMode
        ?? options.parserMetadata?.engineLabelMode
        ?? options.parserMetadata?.engineLayout?.labelMode
        ?? options.parserMetadata?.engineLayout?.engineLabelMode,
    ),
    options.allowThermalTwinInference === true ? "thermal" : "",
  ].join("|");
}

export function engineDescriptorsForData(points = [], profile = null, options: EngineContextOptions = {}) {
  const safePoints = Array.isArray(points) ? points : [];
  if (Array.isArray(points)) {
    const cachedByProfile = engineDescriptorCacheByPoints.get(safePoints);
    const contextKey = descriptorCacheContextKey(options);
    const cachedByContext = cachedByProfile?.get(profile);
    if (cachedByContext?.has(contextKey)) return cachedByContext.get(contextKey);
    const descriptors = buildEngineDescriptorsForData(safePoints, profile, options);
    const nextCache = cachedByProfile ?? new Map();
    const nextContext = cachedByContext ?? new Map();
    nextContext.set(contextKey, descriptors);
    nextCache.set(profile, nextContext);
    if (!cachedByProfile) engineDescriptorCacheByPoints.set(safePoints, nextCache);
    return descriptors;
  }
  return buildEngineDescriptorsForData(safePoints, profile, options);
}

function buildEngineDescriptorsForData(safePoints, profile, options: EngineContextOptions = {}) {
  const detection = twinDetectionResult(safePoints, profile, options);
  if (detection.detected) {
    const labels = twinEngineLabelScheme(profile, options);
    const layout = explicitEngineLayout(profile, options);
    const engineOneSources = layoutCylinderSourceNumbers(layout, 0) ?? [1, 2, 3, 4, 5, 6];
    const engineTwoSources = layoutCylinderSourceNumbers(layout, 1) ?? [7, 8, 9, 10, 11, 12];
    return [
      {
        id: "left",
        engineKey: "engine1",
        engineNumber: 1,
        engineLayoutSource: detection.source,
        label: labels[0].label,
        shortLabel: labels[0].shortLabel,
        index: 0,
        side: "left",
        cylinders: cylinderDescriptorsForSources(safePoints, engineOneSources),
        channels: engineOneChannels,
      },
      {
        id: "right",
        engineKey: "engine2",
        engineNumber: 2,
        engineLayoutSource: detection.source,
        label: labels[1].label,
        shortLabel: labels[1].shortLabel,
        index: 1,
        side: "right",
        cylinders: cylinderDescriptorsForSources(safePoints, engineTwoSources),
        channels: engineTwoChannels,
      },
    ];
  }

  const count = inferredSingleCylinderCount(safePoints, profile);
  const singleSources = layoutCylinderSourceNumbers(explicitEngineLayout(profile, options), 0);
  return [
    {
      id: "engine1",
      engineKey: "engine1",
      engineNumber: 1,
      engineLayoutSource: detection.source,
      label: "Engine",
      shortLabel: "E1",
      index: 0,
      side: "single",
      cylinders: singleSources
        ? cylinderDescriptorsForSources(safePoints, singleSources)
        : cylinderDescriptors(safePoints, 1, count),
      channels: singleEngineChannels,
    },
  ];
}

export function engineCountForData(points = [], profile = null, options: EngineContextOptions = {}) {
  return engineDescriptorsForData(points, profile, options).length;
}

export function hasMultipleEngines(points = [], profile = null, options: EngineContextOptions = {}) {
  return engineCountForData(points, profile, options) > 1;
}

export function engineById(descriptors = [], engineId = "") {
  return descriptors.find((engine) => engine.id === engineId || engine.engineKey === engineId || String(engine.engineNumber) === String(engineId)) ?? null;
}

export function engineCylinderKeys(engine, prefix) {
  const field = `${prefix}Key`;
  return (engine?.cylinders ?? []).map((cylinder) => cylinder[field]).filter(Boolean);
}

export function engineCylinderValue(point, engine, prefix, displayNumber) {
  const cylinder = (engine?.cylinders ?? []).find((candidate) => candidate.number === displayNumber);
  return cylinder ? point?.[cylinder[`${prefix}Key`]] : null;
}

export function engineInfoForSourceNumber(sourceNumber, descriptors = []) {
  const number = Number(sourceNumber);
  if (!Number.isFinite(number)) return { engine: null, cylinder: null };
  for (const engine of descriptors ?? []) {
    const cylinder = (engine.cylinders ?? []).find((candidate) => candidate.sourceNumber === number);
    if (cylinder) return { engine, cylinder };
  }
  return { engine: null, cylinder: null };
}

function normalizeLabelStyle(options: string | EngineContextOptions = {}) {
  const value = typeof options === "string" ? options : options.style;
  return value === ENGINE_LABEL_STYLES.NARRATIVE ? ENGINE_LABEL_STYLES.NARRATIVE : ENGINE_LABEL_STYLES.COMPACT;
}

function engineLabelForStyle(engine, descriptors = [], options: EngineContextOptions = {}) {
  if (!engine || (descriptors?.length ?? 0) <= 1) return "";
  if (normalizeLabelStyle(options) === ENGINE_LABEL_STYLES.NARRATIVE) {
    return String(engine.label ?? engine.shortLabel ?? "Engine").replace(/\s+Engine$/i, "");
  }
  return String(engine.shortLabel ?? engine.label ?? "E").replace(/\s+Engine$/i, "");
}

export function engineScopedCylinderLabel(sourceNumber, descriptors = [], fallbackPrefix = "Cyl", options: EngineContextOptions = {}) {
  const { engine, cylinder } = engineInfoForSourceNumber(sourceNumber, descriptors);
  const number = cylinder?.number ?? Number(sourceNumber);
  if (!Number.isFinite(number)) return String(fallbackPrefix);
  const prefix = engineLabelForStyle(engine, descriptors, options);
  return prefix ? `${prefix} ${fallbackPrefix} ${number}` : `${fallbackPrefix} ${number}`;
}

export function engineCylinderIdentity(sourceNumber, descriptors = []) {
  const { engine, cylinder } = engineInfoForSourceNumber(sourceNumber, descriptors);
  const source = Number(sourceNumber);
  return {
    sourceCylinder: source,
    displayCylinder: cylinder?.number ?? source,
    engineId: engine?.id ?? null,
    engineLabel: engine?.label ?? null,
    engineShortLabel: engine?.shortLabel ?? null,
    cylinderLabel: engineScopedCylinderLabel(source, descriptors, "Cylinder", { style: ENGINE_LABEL_STYLES.NARRATIVE }),
    cylinderShortLabel: engineScopedCylinderLabel(source, descriptors, "Cyl", { style: ENGINE_LABEL_STYLES.NARRATIVE }),
  };
}

export function engineScopedText(value, descriptors = []) {
  if (typeof value !== "string" || descriptors.length < 2) return value;
  return value.replace(/\b(?:(Left|Right|Front|Rear|L|R|F)\s+)?(Cyl(?:inder)?|CHT|EGT)\s*#?\s*(\d+)\b/gi, (match, scope, prefix, source) => {
    if (scope || !engineInfoForSourceNumber(Number(source), descriptors).engine) return match;
    return engineScopedCylinderLabel(Number(source), descriptors, prefix, { style: ENGINE_LABEL_STYLES.NARRATIVE });
  });
}

export function engineNarrativeCylinderLabel(sourceNumber, descriptors = [], fallbackPrefix = "Cylinder") {
  const { engine, cylinder } = engineInfoForSourceNumber(sourceNumber, descriptors);
  const number = cylinder?.number ?? Number(sourceNumber);
  if (!Number.isFinite(number)) return String(fallbackPrefix);
  if (engine && (descriptors?.length ?? 0) > 1) {
    const engineLabel = String(engine.label ?? engine.shortLabel ?? "Engine").replace(/\s+Engine$/i, " engine");
    return `${engineLabel} ${fallbackPrefix} ${number}`;
  }
  return `${fallbackPrefix} ${number}`;
}

export function engineScopedProbeLabel(keyOrLabel, descriptors = [], options: EngineContextOptions = {}) {
  if (keyOrLabel == null) return "";
  const text = String(keyOrLabel ?? "").trim();
  const match = text.match(/\b(EGT|CHT)\s*#?\s*(\d{1,2})\b/i) ?? text.match(/^(egt|cht)(\d{1,2})$/i);
  if (!match) return text;
  const family = match[1].toUpperCase();
  const sourceNumber = Number(match[2]);
  const { engine, cylinder } = engineInfoForSourceNumber(sourceNumber, descriptors);
  const displayNumber = cylinder?.number ?? sourceNumber;
  const prefix = engineLabelForStyle(engine, descriptors, options);
  return prefix ? `${prefix} ${family} ${displayNumber}` : `${family} ${displayNumber}`;
}

export function engineMetricValue(point, engine, metricKey) {
  if (!point || !engine) return null;
  if (metricKey === "egt" || metricKey === "cht") {
    return averageValues(engineCylinderKeys(engine, metricKey).map((key) => point[key]));
  }
  const key = engine.channels?.[metricKey];
  const value = canonicalValueForField(point, key);
  if (key && finite(value)) return value;
  if (engine.side === "single" && finite(canonicalValueForField(point, metricKey))) return canonicalValueForField(point, metricKey);
  return null;
}

export function engineDelta(point, leftEngine, rightEngine, metricKey) {
  const left = engineMetricValue(point, leftEngine, metricKey);
  const right = engineMetricValue(point, rightEngine, metricKey);
  return finite(left) && finite(right) ? right - left : null;
}

export function engineScopedChannelLabel(key, descriptors = [], options: EngineContextOptions = {}) {
  const multiEngine = (descriptors?.length ?? 0) > 1;
  const style = normalizeLabelStyle(options);
  for (const engine of descriptors) {
    const prefix = engineLabelForStyle(engine, descriptors, { style });
    for (const cylinder of engine.cylinders ?? []) {
      if (cylinder.egtKey === key) return prefix ? `${prefix} EGT${style === ENGINE_LABEL_STYLES.NARRATIVE ? " " : ""}${cylinder.number}` : `EGT${cylinder.number}`;
      if (cylinder.chtKey === key) return prefix ? `${prefix} CHT${style === ENGINE_LABEL_STYLES.NARRATIVE ? " " : ""}${cylinder.number}` : `CHT${cylinder.number}`;
    }
    for (const [metric, channelKey] of Object.entries(engine.channels ?? {})) {
      if (canonicalFieldForKey(channelKey) === canonicalFieldForKey(key)) return multiEngine ? `${prefix} ${metricLabel(metric)}` : metricLabel(metric);
    }
  }
  return null;
}

export function aggregateKeyForSingleEngineIndexedChannel(key, descriptors = []) {
  if ((descriptors?.length ?? 0) !== 1 || descriptors[0]?.side !== "single") return null;
  return singleEngineIndexedAggregateChannels[key] ?? null;
}

function metricLabel(metric) {
  return {
    fuelFlow: "Fuel Flow",
    fuelUsed: "Fuel Used",
    fuelPressure: "Fuel Pressure",
    rpm: "RPM",
    map: "MAP",
    oilTemp: "Oil Temp",
    oilPressure: "Oil Pressure",
    amps: "Amps",
    tit: "TIT",
  }[metric] ?? metric;
}

const ENGINE_SUFFIXED_SCALAR_FIELDS = Object.freeze(Array.from(new Set([
  ...Object.values(engineOneChannels),
  ...Object.values(engineTwoChannels),
  "fuelFlowLeft", "fuelFlowRight",
  "fuelUsedLeft", "fuelUsedRight",
  "rpmLeft", "rpmRight",
  "mapLeft", "mapRight",
  "oilTempLeft", "oilTempRight",
  "oilPressureLeft", "oilPressureRight",
  "ampsLeft", "ampsRight",
  "titLeft", "titRight",
]))).filter(Boolean);

export function projectFlightDataForEngine(data, engine) {
  if (!Array.isArray(data) || !engine || engine.side === "single") return data;
  const egtKeys = new Set((engine.cylinders ?? []).map((cylinder) => cylinder.egtKey).filter(Boolean));
  const chtKeys = new Set((engine.cylinders ?? []).map((cylinder) => cylinder.chtKey).filter(Boolean));
  const allEgtKeys = cylinderKeys("egt");
  const allChtKeys = cylinderKeys("cht");
  const engineFields = new Set(Object.values(engine.channels ?? {}).filter(Boolean));
  const suffixedFieldsToNull = ENGINE_SUFFIXED_SCALAR_FIELDS.filter((field) => !engineFields.has(field));
  return data.map((point) => {
    const next = { ...point };
    allEgtKeys.forEach((key) => { if (!egtKeys.has(key)) next[key] = null; });
    allChtKeys.forEach((key) => { if (!chtKeys.has(key)) next[key] = null; });
    next.egt = averageValues([...egtKeys].map((key) => point[key as string]));
    next.cht = averageValues([...chtKeys].map((key) => point[key as string]));
    suffixedFieldsToNull.forEach((field) => { next[field] = null; });
    ENGINE_SCALAR_METRICS.forEach((metric) => {
      const sourceKey = engine.channels?.[metric];
      const value = sourceKey ? canonicalValueForField(point, sourceKey) : null;
      next[metric] = Number.isFinite(value) ? value : null;
      if (sourceKey) next[sourceKey] = next[metric];
    });
    return next;
  });
}
